/**
 * The onboarding script — one entry per step, walked by
 * components/onboarding-tour.tsx against the sample trip.
 *
 * Step kinds:
 *   • spotlight — driver.js dims the screen and points at a
 *     `[data-tour="<target>"]` element (or centers the popover when
 *     there's no target).
 *   • task — the user does something real in the sample trip (add an
 *     event, vote…). The screen stays interactive; a slim hint bar says
 *     what to do and the step advances on the matching demo event.
 *
 * Every step can be passed with Next. On a hands-on step, Next skips the
 * action (and its follow-up steps, via `skipTo`) instead of waiting for it.
 *
 * `path` is where the controller navigates when the step starts. `null`
 * means "stay put" — used right after a task, when the app's own flow
 * (e.g. saving the add-event sheet) already navigates back.
 */

import type { Side } from "driver.js";

import { getLastCreatedActivityId, type DemoEvent } from "@/lib/demo/demo-store";
import { DEMO_TRIP_ID } from "@/lib/demo/demo-trip";

export interface TourStep {
  id: string;
  kind: "spotlight" | "task";
  path: string | null;
  /**
   * What to point at (spotlight) or pulse (task): a `data-tour` attribute
   * value, or a function returning a full CSS selector for targets only
   * known at runtime.
   */
  target?: string | (() => string | null);
  title: string;
  body: string;
  side?: Side;
  /** Label of the Next button (defaults to "Next"). Every step has one. */
  nextLabel?: string;
  /** Spotlight only: tapping the highlighted element continues the tour (does the action). */
  advanceOnTargetClick?: boolean;
  /** Advance when the sample trip emits this event. */
  advanceOnEvent?: DemoEvent;
  /**
   * Hands-on steps: pressing Next *instead of* doing the action jumps to this
   * step id (skipping the action's follow-up steps). Defaults to the next step.
   */
  skipTo?: string;
  /** Route to go to before jumping to `skipTo` (when that step has `path: null`). */
  skipPath?: string;
}

const TRIP = `/trip/${DEMO_TRIP_ID}`;

/** Resolve a step's target to a CSS selector (null → centered popover). */
export function targetSelector(step: TourStep): string | null {
  if (!step.target) return null;
  if (typeof step.target === "function") return step.target();
  return `[data-tour="${step.target}"]`;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: "welcome",
    kind: "spotlight",
    path: "/dashboard",
    target: "demo-trip-card",
    title: "Welcome to Travelix",
    body: "Here's a sample trip to play with. Nothing you do in it is saved, so tap around freely.",
    nextLabel: "Open it",
  },
  {
    id: "hero",
    kind: "spotlight",
    path: `${TRIP}/timeline`,
    target: "trip-hero",
    title: "Every trip has a home",
    body: "Dates, destinations and the group live up here. The gear icon holds members, currency and budget.",
    nextLabel: "Next",
  },
  {
    id: "tabs",
    kind: "spotlight",
    path: `${TRIP}/timeline`,
    target: "trip-tabs",
    side: "top",
    title: "Four sections",
    body: "Timeline for the schedule, Plan for ideas and polls, Expenses for money, Docs for tickets.",
    nextLabel: "Next",
  },
  {
    id: "add-event",
    kind: "spotlight",
    path: `${TRIP}/timeline`,
    target: "add-event",
    side: "left",
    title: "Your turn: add an event",
    body: "Tap “Add event” to put something on the timeline — or tap Next to skip it.",
    advanceOnTargetClick: true,
    skipTo: "plan",
  },
  {
    id: "add-event-task",
    kind: "task",
    path: null,
    title: "Name it and save",
    body: "Anything works — try “Coffee at the hotel”. Then tap “Add to timeline”.",
    advanceOnEvent: "activity:created",
    skipTo: "plan",
  },
  {
    id: "timeline",
    kind: "spotlight",
    path: null,
    target: () => {
      const id = getLastCreatedActivityId();
      return id ? `[data-activity-id="${id}"]` : null;
    },
    title: "Added for everyone",
    body: "The whole group sees it right away. Tap an event to edit or remove it.",
    nextLabel: "Next",
  },
  {
    id: "plan",
    kind: "spotlight",
    path: `${TRIP}/plan`,
    target: "plan-sections",
    title: "Plan together",
    body: "Ideas is the group wishlist, Polls settle debates, Packing splits who brings what.",
    nextLabel: "Next",
  },
  {
    id: "vote-task",
    kind: "task",
    path: null,
    target: "plan-sections",
    title: "Break the tie",
    body: "Open Polls and vote on where to eat tonight.",
    advanceOnEvent: "poll:voted",
  },
  {
    id: "expenses",
    kind: "spotlight",
    path: `${TRIP}/expenses`,
    target: "expenses-summary",
    title: "Shared money, sorted",
    body: "Log what anyone pays. The trip total, per-person share and budget update live.",
    nextLabel: "Next",
  },
  {
    id: "add-expense",
    kind: "spotlight",
    path: `${TRIP}/expenses`,
    target: "expense-fab",
    side: "left",
    title: "Your turn: log an expense",
    body: "Tap “Add” to log something the group spent — or tap Next to skip it.",
    advanceOnTargetClick: true,
    skipTo: "balances",
  },
  {
    id: "add-expense-task",
    kind: "task",
    path: null,
    title: "Log it",
    body: "Pick “Add expense”, enter an amount and save — it's split for you.",
    advanceOnEvent: "expense:added",
    skipTo: "balances",
    skipPath: `${TRIP}/expenses`,
  },
  {
    id: "balances",
    kind: "spotlight",
    path: null,
    target: "expenses-tabs",
    title: "Who owes who",
    body: "This tab shows what you owe and who owes you — pay or record it right from here.",
    nextLabel: "Next",
  },
  {
    id: "done",
    kind: "spotlight",
    path: null,
    title: "You're all set",
    body: "Create your first trip and invite your friends. You can replay this tour anytime from your profile.",
    nextLabel: "Create my trip",
  },
];
